import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { Loader2, RefreshCw, PlusCircle, Trash2, Mail } from 'lucide-react';
import { z } from 'zod';
import { RoleGate } from '@/components/auth/RoleGate';
import * as InvestorMappingService from '@/services/investorMappingService';
import { InvestorInviteDialog } from './InvestorInviteDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const emailSchema = z.string().trim().email({ message: 'Invalid email address' });

interface Mapping {
  id: string;
  user_id: string;
  shareholder_id: string;
  created_at: string;
}

interface Shareholder {
  id: string;
  name: string;
  email: string | null;
}

interface MappingWithDetails extends Mapping {
  shareholder_name?: string;
  user_email?: string;
}

interface InvestorMappingToolProps {
  companyId: string;
}

interface Company {
  id: string;
  name: string;
}

export function InvestorMappingTool({ companyId }: InvestorMappingToolProps) {
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [shareholderId, setShareholderId] = useState('');
  const [shareholders, setShareholders] = useState<Shareholder[]>([]);
  const [mappings, setMappings] = useState<MappingWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [company, setCompany] = useState<Company | null>(null);

  async function loadData() {
    try {
      setLoading(true);

      // Get company details
      const { data: companyData, error: companyError } = await supabase
        .from('companies')
        .select('id, name')
        .eq('id', companyId)
        .single();

      if (companyError) throw companyError;
      setCompany(companyData);

      // Get shareholders for this company
      const { data: shareholdersData, error: shareholdersError } = await supabase
        .from('shareholders')
        .select('id, name, email')
        .eq('company_id', companyId)
        .order('name');

      if (shareholdersError) throw shareholdersError;

      // Get all mappings with shareholder details
      const { data: mappingsData, error: mappingsError } = await supabase.from(
        'investor_identities'
      ).select(`
          id,
          user_id,
          shareholder_id,
          created_at
        `);

      if (mappingsError) throw mappingsError;

      setShareholders(shareholdersData || []);

      // Enhance mappings with shareholder names
      const enhancedMappings = (mappingsData || []).map((mapping) => {
        const shareholder = (shareholdersData || []).find((s) => s.id === mapping.shareholder_id);
        return {
          ...mapping,
          shareholder_name: shareholder?.name || 'Unknown',
        };
      });

      setMappings(enhancedMappings);
    } catch (err: any) {
      console.error('Error loading investor mapping data:', err);
      toast({
        title: 'Error loading data',
        description: err.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }

  async function createMapping() {
    // Validate inputs
    const emailValidation = emailSchema.safeParse(email);
    if (!emailValidation.success) {
      toast({
        title: 'Invalid email',
        description: emailValidation.error.errors[0].message,
        variant: 'destructive',
      });
      return;
    }

    if (!shareholderId) {
      toast({
        title: 'Missing information',
        description: 'Please select a shareholder',
        variant: 'destructive',
      });
      return;
    }

    setCreating(true);
    try {
      await InvestorMappingService.createInvestorIdentity({
        email: email.trim(),
        shareholderId,
      });

      toast({
        title: 'Success',
        description: 'Investor identity linked successfully',
      });

      // Reset form and reload
      setEmail('');
      setShareholderId('');
      loadData();
    } catch (err: any) {
      console.error('Error creating mapping:', err);
      toast({
        title: 'Error creating mapping',
        description: err.message || 'Failed to create mapping',
        variant: 'destructive',
      });
    } finally {
      setCreating(false);
    }
  }

  async function deleteMapping(mappingId: string) {
    try {
      await InvestorMappingService.deleteInvestorIdentity(mappingId);

      toast({
        title: 'Success',
        description: 'Mapping deleted successfully',
      });

      loadData();
    } catch (err: any) {
      console.error('Error deleting mapping:', err);
      toast({
        title: 'Error deleting mapping',
        description: err.message,
        variant: 'destructive',
      });
    } finally {
      setDeleteId(null);
    }
  }

  useEffect(() => {
    if (companyId) {
      loadData();
    }
  }, [companyId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <RoleGate allowedRoles={['owner', 'admin']} companyId={companyId}>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle>Investor Identity Mapping</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Link user accounts to shareholder identities for investor portal access
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setInviteDialogOpen(true)}
              disabled={loading || !company}
            >
              <Mail className="h-4 w-4 mr-2" />
              Send Invite
            </Button>
            <Button variant="outline" size="sm" onClick={loadData} disabled={loading}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Create Mapping Form */}
          <div className="grid gap-4 md:grid-cols-[1fr_1fr_auto]">
            <div className="space-y-2">
              <Label htmlFor="investor-email">Investor Email</Label>
              <Input
                id="investor-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="investor@example.com"
                disabled={creating}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="shareholder">Shareholder</Label>
              <Select value={shareholderId} onValueChange={setShareholderId} disabled={creating}>
                <SelectTrigger id="shareholder">
                  <SelectValue placeholder="Select shareholder..." />
                </SelectTrigger>
                <SelectContent>
                  {shareholders.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name} {s.email ? `(${s.email})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button onClick={createMapping} disabled={creating || !email || !shareholderId}>
                {creating ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <PlusCircle className="h-4 w-4 mr-2" />
                )}
                Create Mapping
              </Button>
            </div>
          </div>

          {/* Mappings Table */}
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Shareholder</TableHead>
                  <TableHead>User ID</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {mappings.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                      No investor mappings yet. Create one above to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  mappings.map((m) => (
                    <TableRow key={m.id}>
                      <TableCell className="font-medium">{m.shareholder_name}</TableCell>
                      <TableCell className="font-mono text-sm">
                        {m.user_id.slice(0, 8)}...
                      </TableCell>
                      <TableCell>{new Date(m.created_at).toLocaleDateString()}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => setDeleteId(m.id)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Investor Invite Dialog */}
      {company && (
        <InvestorInviteDialog
          open={inviteDialogOpen}
          onOpenChange={setInviteDialogOpen}
          companyId={companyId}
          companyName={company.name}
          shareholders={shareholders}
          onSuccess={loadData}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Mapping?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the investor's access to the portal for this shareholder identity.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteId && deleteMapping(deleteId)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </RoleGate>
  );
}
